import {
  ApiError,
  sendNotification,
  useApiClient,
  useAsyncCache,
  useFormMetadataSync,
  useTranslation,
} from '@msdk/common'
import {
  EntityModel,
  FormModel,
  GenericFieldValue,
  LookupValue,
  readEntityModelByName,
  readUserData,
  StringValue,
} from '@msdk/definitions'
import {
  Box,
  ButtonIcon,
  CheckIcon,
  FormField,
  FormFieldLabel,
  Spinner,
  styled,
  TextInput,
  VisibilityOffIcon,
  VisibilityOnIcon,
} from '@msdk/ui'
import { InputAdornment } from '@mui/material'
import { ChangeEvent, useState } from 'react'
import { useWizard } from 'react-use-wizard'

import { FooterPopup } from './FooterPopup'
import { HeaderPopup } from './HeaderPopup'
import {
  ModalStepProps,
  StyledContainer,
  StyledEditContent,
  StyledList,
  StyledListElement,
  StyledListItem,
  StyledMessageTypography,
} from './shared'

const StyledSpinnerContainer = styled(Box)`
  width: 100%;
  height: 100%;
  position: absolute;
  align-content: center;
  z-index: 1;
  text-align: center;
`

const StyledSpinner = styled(Spinner)`
  width: 100px !important;
  height: 100px !important;
`

interface PasswordPopupProps extends ModalStepProps {
  nickname: StringValue | null
}

export function PasswordPopup(props: PasswordPopupProps) {
  const { nextStep, previousStep } = useWizard()
  const t = useTranslation()
  const formModel = useFormMetadataSync('USER_PASSCODE', 'USER_NICKNAME_HASH_FORM') as FormModel
  const apiClient = useApiClient()
  const [value, setValue] = useState<StringValue | null>(null)
  const [confirmValue, setConfirmValue] = useState<StringValue | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const cache = useAsyncCache()
  const password = value ? value.toStringForView() : ''
  const confirmpassword = confirmValue ? confirmValue.toStringForView() : ''

  const isAllSame = (str: string): boolean => {
    return str.length > 0 && str.split('').every((c) => c === str[0])
  }

  const isSequential = (str: string): boolean => {
    if (str.length < 2) return false
    const step = str.charCodeAt(1) - str.charCodeAt(0)
    if (step !== 1 && step !== -1) return false
    for (let i = 1; i < str.length; i++) {
      if (str.charCodeAt(i) - str.charCodeAt(i - 1) !== step) return false
    }
    return true
  }

  const requirements: { id: string; label: string; test: () => boolean }[] = [
    {
      id: 'length',
      label: t('LBL_NGID_PASSCODE_RULE1', '5-20 characters'),
      test: () => password.length >= 5 && password.length <= 20,
    },
    {
      id: 'alphanumeric',
      label: t('LBL_NGID_PASSCODE_RULE2', 'Not all-same or sequential (no 111111, 123456)'),
      test: () => password.length > 0 && !isAllSame(password) && !isSequential(password),
    },
    {
      id: 'unique',
      label: t('LBL_NGID_PASSCODE_RULE3', 'Password and confirmation must match'),
      test: () => password.length > 0 && password === confirmpassword,
    },
  ]

  const disableContinue = !(requirements[0].test() && requirements[1].test() && requirements[2].test()) || isLoading

  const onPasswordChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value
    const fieldValue = newValue !== '' ? new StringValue(newValue) : null
    setValue(fieldValue)
    if (!fieldValue) {
      setShowPassword(false)
    }
  }

  const onConfirmChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value
    const fieldValue = newValue !== '' ? new StringValue(newValue) : null
    setConfirmValue(fieldValue)
    if (!fieldValue) {
      setShowConfirmPassword(false)
    }
  }

  const saveCredentials = async (nickname: string, passcode: string): Promise<boolean> => {
    const entityModel = readEntityModelByName('USER_PASSCODE') as EntityModel
    const fieldValues: Record<string, GenericFieldValue> = {}
    const loggedUserLookup = new LookupValue(readUserData().user_id)
    loggedUserLookup.entityName = 'USER'
    fieldValues.USER = loggedUserLookup
    fieldValues.FRIENDLY_NAME = new StringValue(nickname)
    fieldValues['FE.PASSCODE'] = new StringValue(passcode)
    const createResult = await apiClient.entity.createEntity(
      'USM',
      entityModel,
      undefined,
      false,
      formModel,
      fieldValues,
    )
    const res = await apiClient.entity.saveEntities('USM', entityModel, createResult.entities, formModel)
    if (res instanceof ApiError) {
      console.error('[PasswordPopup] - Error saving credentials', res)
      sendNotification({
        message: t('LBL_NGID_ERROR_SAVING_CREDENTIALS', 'Error saving credentials'),
        status: 'error',
      })
      return false
    }
    return true
  }

  const handleStep = async () => {
    setIsLoading(true)
    const saveOk = await saveCredentials(props.nickname?.toStringForView() ?? '', password)
    setIsLoading(false)
    if (saveOk) {
      cache.deleteEntry(`isUserDialysisMachineSignedIn`)
      await cache.getOrCreate(`isUserDialysisMachineSignedIn`, () => Promise.resolve(true))
      sendNotification({
        message: t(
          'LBL_NGID_NICK_PASS_NOTIFIC_MSG',
          'You can now log in to the dialysis machine by entering your nickname and passcode.',
        ),
        status: 'success',
        title: t('LBL_NGID_NICK_PASS_NOTIFIC_TITLE', 'Nickname and passcode is active'),
      })
      nextStep()
    }
  }

  return (
    <>
      {isLoading && (
        <StyledSpinnerContainer>
          <StyledSpinner color="info" />
        </StyledSpinnerContainer>
      )}
      <HeaderPopup title={t('LBL_NGID_NICK_PASS_TITLE', 'Nickname and passcode')} modalRef={props.modalRef} />
      <StyledEditContent>
        <StyledMessageTypography variant="body1">
          {t(
            'LBL_NGID_PASSCODE_DESCR',
            'Passcode is the access code used to login into the machine together with your nickname.',
          )}
        </StyledMessageTypography>
        <FormField required size="small">
          <FormFieldLabel>{t('LBL_NGID_PASSCODE', 'Passcode')}</FormFieldLabel>
          <TextInput
            label={t('LBL_NGID_PASSCODE', 'Passcode')}
            inputMode="text"
            size="small"
            onChange={onPasswordChange}
            value={value ? value.toStringForView() : ''}
            type={showPassword ? 'text' : 'password'}
            endAdornment={
              value && (
                <InputAdornment position="end">
                  <ButtonIcon
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((s) => !s)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOnIcon /> : <VisibilityOffIcon />}
                  </ButtonIcon>
                </InputAdornment>
              )
            }
          />
        </FormField>
        <FormField required size="small">
          <FormFieldLabel>{t('LBL_NGID_CONFIRM_PASSCODE', 'Confirm passcode')}</FormFieldLabel>
          <TextInput
            label={t('LBL_NGID_CONFIRM_PASSCODE', 'Confirm passcode')}
            inputMode="text"
            size="small"
            onChange={onConfirmChange}
            value={confirmValue ? confirmValue.toStringForView() : ''}
            type={showConfirmPassword ? 'text' : 'password'}
            endAdornment={
              confirmValue && (
                <InputAdornment position="end">
                  <ButtonIcon
                    aria-label="toggle password visibility"
                    onClick={() => setShowConfirmPassword((s) => !s)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOnIcon /> : <VisibilityOffIcon />}
                  </ButtonIcon>
                </InputAdornment>
              )
            }
          />
        </FormField>
        <StyledContainer>
          <StyledListItem>{t('LBL_NGID_PASSCODE_REQUIREMENTS', 'The passcode requirements:')}</StyledListItem>
          <StyledList>
            {requirements.map((r) => {
              const satisfied = r.test()
              const notSatisfied = password.length > 0 && !satisfied
              return (
                <StyledListElement key={r.id} $satisfied={satisfied} $notSatisfied={notSatisfied}>
                  {satisfied && (
                    <CheckIcon aria-hidden fontSize="small" style={{ position: 'absolute', left: '-1.25em' }} />
                  )}
                  {r.label}
                </StyledListElement>
              )
            })}
          </StyledList>
        </StyledContainer>
      </StyledEditContent>
      <FooterPopup
        secondary={{ label: t('LBL_NGID_BACK_BTN', 'Back'), onClick: previousStep }}
        primary={{
          label: t('LBL_NGID_NICK_PASS_SAVE_BTN', 'Set up nickname and passcode'),
          onClick: handleStep,
          disabled: disableContinue,
        }}
      />
    </>
  )
}
